/**
 * Tabellen-Parser für Copy-Paste (TSV aus Excel/Sheets), CSV (Komma, Semikolon,
 * Pipe) und einfache Listen (docs/05 §1–2). Ohne Abhängigkeiten.
 */

export interface ParsedTable {
  delimiter: string;
  /** true, wenn die erste Zeile wie eine Überschrift aussieht */
  hasHeader: boolean;
  header: string[];
  rows: string[][];
  /** Zeilen waren überwiegend „Name: …" — die erste Spalte ist sicher der Team-/Mannschaftsname */
  colonForm?: boolean;
}

const CANDIDATES = ['\t', ';', ',', '|'];

export function detectDelimiter(lines: string[]): string {
  let best = '\t';
  let bestScore = -1;
  for (const d of CANDIDATES) {
    const counts = lines.map((l) => countOutsideQuotes(l, d));
    const nonZero = counts.filter((c) => c > 0);
    if (nonZero.length === 0) continue;
    // Bonus für Konsistenz: gleiche Anzahl in möglichst vielen Zeilen
    const mode = modeOf(nonZero);
    const consistent = nonZero.filter((c) => c === mode).length;
    const score = consistent * 10 + nonZero.length;
    if (score > bestScore) {
      bestScore = score;
      best = d;
    }
  }
  return best;
}

function modeOf(nums: number[]): number {
  const m = new Map<number, number>();
  for (const n of nums) m.set(n, (m.get(n) ?? 0) + 1);
  return [...m.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 0;
}

function countOutsideQuotes(line: string, d: string): number {
  let inQ = false;
  let n = 0;
  for (const ch of line) {
    if (ch === '"') inQ = !inQ;
    else if (ch === d && !inQ) n++;
  }
  return n;
}

export function splitLine(line: string, d: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i] as string;
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else inQ = !inQ;
    } else if (ch === d && !inQ) {
      out.push(cur);
      cur = '';
    } else cur += ch;
  }
  out.push(cur);
  return out.map((c) => c.trim());
}

/** Zeilen, die nur Trenner/Leerraum/Summen enthalten, werden ignoriert */
function isNoise(cells: string[]): boolean {
  const joined = cells.join('').trim();
  if (joined === '') return true;
  if (/^[-=_~·•\s]+$/.test(joined)) return true;
  const first = (cells[0] ?? '').toLowerCase();
  if (/^(summe|gesamt|total|anzahl)\b/.test(first) && cells.slice(1).every((c) => c === '' || /^[\d.,]+$/.test(c))) return true;
  return false;
}

/**
 * Überschrift = ganze Zelle ist ein bekanntes Wort („Club", „Teamname",
 * „Spieler 2"). Bewusst kein Präfix-Match: „Club Ulm" oder „Team Rocket" sind
 * Daten, keine Überschriften.
 */
const HEADER_WORDS =
  /^(team|teamname|team ?name|mannschaft|verein|club|ort|stadt|city|e-?mail|mail|telefon|tel|phone|handy|mobil|setzung|seed|rang|rank|ranking|bezahlt|paid|startgeld|notiz|note|bemerkung|kommentar|comment|nr\.?|#|nummer|bewerb|division|kategorie|category|klasse|teilnehmer|participant|namen|names|players)$|^(nome|cognome|surname|given ?name|family ?name|familienname|pr[ée]nom|squadra|nome ?squadra|societ[aà]|comune|telefono|cellulare|contatto|kontakt|contact)$|^(name|spieler|player|partner|vorname|nachname|first ?name|last ?name)\s*[1-4]?$/i;

export function looksLikeHeader(cells: string[], rest: string[][]): boolean {
  const hits = cells.filter((c) => HEADER_WORDS.test(c)).length;
  if (hits >= 1 && hits >= Math.ceil(cells.filter((c) => c !== '').length / 3)) return true;
  // Überschrift hat keine Zahlen/E-Mails, Datenzeilen schon
  const headerHasData = cells.some((c) => /@/.test(c) || /^\d+$/.test(c));
  const restHasData = rest.some((r) => r.some((c) => /@/.test(c) || /^\d+$/.test(c)));
  return !headerHasData && restHasData && hits > 0;
}

/**
 * „Name: a; b" → „Name<Tab>a<Tab>b": Der Doppelpunkt trennt den ersten Wert
 * (Team, Mannschaft) vom Rest, dahinter Semikolon/Komma/Schrägstrich/„und"
 * zwischen den Personen. Uhrzeiten („10:30") und URLs bleiben unangetastet.
 */
export function normalizeColonForm(text: string, delimiter = '\t'): string {
  return normalizeColonLines(text.replace(/\r\n?/g, '\n').split('\n'), delimiter).lines.join('\n');
}

function normalizeColonLines(lines: string[], delimiter: string): { lines: string[]; matched: number } {
  let matched = 0;
  const out = lines.map((line) => {
    const m = /^\s*([^:\t;,|]{1,60}?)\s*:\s*(.+?)\s*$/.exec(line);
    if (!m) return line;
    const head = m[1] ?? '';
    const rest = m[2] ?? '';
    if (/^\d+$/.test(head) || /^[\d/]/.test(rest) || /^https?$/i.test(head)) return line;
    matched++;
    const parts = rest.split(/\s*[;,|]\s*|\s+\/\s+|\s+und\s+|\s+&\s+|\s+\+\s+/i).filter(Boolean);
    return [head, ...parts].join(delimiter);
  });
  return { lines: out, matched };
}

/** Zeile besteht nur aus Überschriftswörtern („Teamname; Spieler 1; Spieler 2") — auch mitten im Text */
function isHeaderRow(cells: string[]): boolean {
  const filled = cells.filter((c) => c !== '');
  return filled.length >= 2 && filled.every((c) => HEADER_WORDS.test(c));
}

export function parseTable(text: string): ParsedTable {
  const raw = text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((l) => l.trim() !== '');
  if (raw.length === 0) return { delimiter: '\t', hasHeader: false, header: [], rows: [] };
  // Trenner zuerst am Original bestimmen, dann Doppelpunkt-Zeilen mit demselben Trenner umschreiben —
  // sonst hätte „test: Daniel; Max" plötzlich Tabs, während der Rest Semikolons nutzt
  const delimiter = detectDelimiter(raw);
  const norm = normalizeColonLines(raw, delimiter);
  const lines = norm.lines;
  const colonForm = norm.matched > 0 && norm.matched >= raw.length / 2;
  let rows = lines.map((l) => splitLine(l, delimiter)).filter((r) => !isNoise(r));
  const width = Math.max(...rows.map((r) => r.length));
  rows = rows.map((r) => [...r, ...Array.from({ length: width - r.length }, () => '')]);
  const first = rows[0] ?? [];
  const hasHeader = rows.length > 1 && looksLikeHeader(first, rows.slice(1));
  // Überschriften, die jemand mitten in die Liste kopiert hat, sind keine Teams
  const body = (hasHeader ? rows.slice(1) : rows).filter((r) => !isHeaderRow(r));
  return {
    delimiter,
    hasHeader,
    header: hasHeader ? first : first.map((_, i) => `Spalte ${i + 1}`),
    rows: body,
    ...(colonForm ? { colonForm: true } : {}),
  };
}
